import {
  ChangeEvent,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import Button from 'ui/components/common/Button';
import { OTP_EXPIRY } from 'utils/constants';
import { cn } from 'utils/helpers';

import OTPInput from './OTPInput';

interface PROPS {
  onSubmit: () => void;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onResendOtp: () => void;
  disabled?: boolean;
  value: string;
  error?: boolean;
  success?: boolean;
  children?: (props: { disabled: boolean; resendOtp: () => void }) => ReactNode;
}

const OTPForm: FC<PROPS> = ({
  onSubmit,
  onChange,
  onResendOtp,
  disabled = false,
  value,
  error = false,
  success = false,
  children,
}) => {
  const [timeRemaining, setTimeRemaining] = useState(OTP_EXPIRY);
  const [canResend, setCanResend] = useState<boolean | null>(null);

  const timer = useCallback(() => {
    setTimeout(() => setTimeRemaining((prevTime) => prevTime - 1), 1000);
  }, []);

  useEffect(() => {
    if (!(timeRemaining <= 0)) {
      timer();

      if (timeRemaining === OTP_EXPIRY - 10) setCanResend(true);
    } else {
      clearTimeout(timer as unknown as NodeJS.Timeout);
      setCanResend(true);
    }
  }, [timeRemaining, timer]);

  const handleResendOtp = () => {
    setTimeRemaining(OTP_EXPIRY);
    setCanResend(true);
    onResendOtp();
  };

  const _disable = useMemo(() => disabled || !canResend, [canResend, disabled]);

  return (
    <div className="flex flex-col items-center">
      <form onSubmit={onSubmit} className="text-left">
        <OTPInput
          value={value}
          onChange={onChange}
          error={error}
          success={success}
        />
      </form>
      <div className={cn(error ? 'mt-6' : 'mt-10')}>
        <p className="text-[12px]">
          Resend OTP code in {timeRemaining} seconds
        </p>
        {children ? (
          children({
            disabled: _disable,
            resendOtp: handleResendOtp,
          })
        ) : (
          <Button
            type="button"
            className="mx-auto"
            label="Resend Code"
            disabled={_disable}
            onClick={handleResendOtp}
            intent="link"
          />
        )}
      </div>
    </div>
  );
};

export default OTPForm;
